/**
 * Trade API Service
 * Handles all trade-related API calls to the backend
 * Replaces localStorage-based trade storage
 *
 * All routes that take a path trade_id (GET/PUT/DELETE /trades/{trade_id}, POST /trades/{trade_id}/close)
 * require the trade's DB UUID (trade.id from list/detail responses). Do not use broker_trade_id or
 * any other identifier.
 */

import { authenticatedFetch } from "./apiClient";
import { throwIfNotOk } from "../utils/apiRequestError";
import { parseApiErrorBody } from "../utils/parseApiError";
import type { TradePeriodLocalRangeQuery } from "../types/tradePeriodApiContract";
import { appendTradePeriodLocalParams } from "../utils/notificationPeriod";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validateTradeIdUuid(tradeId: string): void {
  if (!tradeId || !UUID_REGEX.test(tradeId.trim())) {
    throw new Error(
      "Invalid trade ID format; expected UUID. Use the trade's id from the API, not broker_trade_id."
    );
  }
}

// Backend API base URL - loaded from environment variable
// For production: Set REACT_APP_BACKEND_API_URL in .env file
// For development: Defaults to localhost:8000
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000/api";

/**
 * Backend Trade interface (matches backend response)
 */
export interface BackendTrade {
  id: string;
  user_id: string;
  broker_account_code: string;
  symbol: string;
  asset_class?: string | null;
  trade_type: "buy" | "sell";
  status: "open" | "closed";
  entry_date: string; // ISO 8601
  entry_price: number;
  entry_quantity: number;
  entry_fees: number;
  entry_value: number;
  broker_entry_order_id: string | null;
  exit_date: string | null; // ISO 8601
  exit_price: number | null;
  exit_quantity: number | null;
  exit_fees: number | null;
  exit_value: number | null;
  broker_exit_order_id: string | null;
  gross_pnl: number | null;
  net_pnl: number | null;
  return_percent: number | null;
  roi: number | null;
  strategy: string | null;
  notes: string | null;
  pre_trade_emotion?: string | null;
  during_trade_emotion?: string | null;
  post_trade_emotion?: string | null;
  tags: string[];
  setup_tags?: string[];
  mistake_tags?: string[];
  broker_synced: boolean;
  broker_trade_id: string | null;
  signal_event_id: string | null;
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
  take_profit_price?: number | null;
  stop_loss_price?: number | null;
  /** Risk/loss amount at SL (from DB) */
  loss_potential?: number | null;
  /** Profit amount at TP (from DB) */
  profit_potential?: number | null;
  position_semantics?: {
    close_kind?: "full" | "partial" | null;
    closed_quantity?: number | null;
    original_entry_quantity?: number | null;
    remaining_quantity?: number | null;
    lot_key?: string | null;
    is_remainder_open?: boolean;
    is_dust?: boolean;
    is_scratch?: boolean;
    shares_exit_fill?: boolean;
    related_trade_ids?: string[];
  } | null;
}

/**
 * Trades list response
 */
export interface TradesListResponse {
  trades: BackendTrade[];
  total: number;
  limit: number;
  offset: number;
}

import type { InstrumentSetupSummary } from "../types/instrumentSetup";

/**
 * Sync trades response
 */
export interface SyncTradesResponse {
  orders: any[]; // Raw broker orders
  sync_stats: {
    created: number;
    updated: number;
    skipped: number;
    total: number;
  };
  instrument_setup?: InstrumentSetupSummary;
}

/**
 * Create trade request
 */
export interface CreateTradeRequest {
  broker_account_code: string;
  symbol: string;
  trade_type: "buy" | "sell";
  status: "open" | "closed";
  entry_date: string; // ISO 8601
  entry_price: number;
  entry_quantity: number;
  entry_fees?: number;
  broker_entry_order_id?: string;
  exit_date?: string; // ISO 8601 (required if status is 'closed')
  exit_price?: number; // Required if status is 'closed'
  exit_quantity?: number; // Required if status is 'closed'
  exit_fees?: number;
  broker_exit_order_id?: string;
  strategy?: string;
  notes?: string;
  tags?: string[];
  setup_tags?: string[];
  mistake_tags?: string[];
  broker_trade_id?: string;
  signal_event_id?: string;
  pre_trade_emotion?: string;
  during_trade_emotion?: string;
  post_trade_emotion?: string;
}

/**
 * Update trade request
 */
export interface UpdateTradeRequest {
  symbol?: string;
  trade_type?: "buy" | "sell";
  status?: "open" | "closed";
  entry_date?: string;
  entry_price?: number;
  entry_quantity?: number;
  entry_fees?: number;
  exit_date?: string; // ISO 8601
  exit_price?: number;
  exit_quantity?: number;
  exit_fees?: number;
  strategy?: string;
  notes?: string | null;
  tags?: string[];
  setup_tags?: string[];
  mistake_tags?: string[];
  pre_trade_emotion?: string | null;
  during_trade_emotion?: string | null;
  post_trade_emotion?: string | null;
  signal_event_id?: string;
}

/** Timeframe values supported by the trades list endpoint (same as statistics). */
export type TradesTimeframe = "today" | "7-days" | "14-days" | "30-days" | "all-time";

/**
 * Fetch trades from backend.
 * Supports timeframe, ISO start_date/end_date, or local_start_date/local_end_date (+ optional timezone).
 * Do not combine ISO dates with local_* (API returns 400).
 */
export const fetchTrades = async (
  filters?: {
    broker_account_code?: string;
    broker_name?: string;
    /** Standard timeframe: today | 7-days | 14-days | 30-days | all-time (default if no dates). */
    timeframe?: TradesTimeframe;
    start_date?: Date;
    end_date?: Date;
    /** Mutually exclusive with start_date/end_date/timeframe. */
    local_period?: TradePeriodLocalRangeQuery;
    symbol?: string;
    status?: "open" | "closed";
    limit?: number;
    offset?: number;
    /** Optional cache-bust value for forcing fresh responses after resync */
    cache_bust?: number;
  }
): Promise<TradesListResponse> => {
  const params = new URLSearchParams();

  if (filters?.broker_account_code) {
    params.append("broker_account_code", filters.broker_account_code);
  }
  if (filters?.broker_name) {
    params.append("broker_name", filters.broker_name.toLowerCase());
  }
  if (filters?.local_period) {
    appendTradePeriodLocalParams(params, filters.local_period);
  } else {
    if (filters?.timeframe) {
      params.append("timeframe", filters.timeframe);
    }
    if (filters?.start_date) {
      params.append("start_date", filters.start_date.toISOString());
    }
    if (filters?.end_date) {
      params.append("end_date", filters.end_date.toISOString());
    }
  }
  if (filters?.symbol) {
    params.append("symbol", filters.symbol);
  }
  if (filters?.status) {
    params.append("status", filters.status);
  }
  if (filters?.limit) {
    params.append("limit", filters.limit.toString());
  }
  if (filters?.offset) {
    params.append("offset", filters.offset.toString());
  }
  if (typeof filters?.cache_bust === "number") {
    params.append("_ts", String(filters.cache_bust));
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/trades?${queryString}` : "/trades";

  const response = await authenticatedFetch(endpoint, {
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      "Pragma": "no-cache",
      "Expires": "0",
    },
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch trades: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch single trade by ID (trade_id must be the trade's DB UUID, e.g. from trade.id in list response).
 */
export const fetchTrade = async (tradeId: string): Promise<BackendTrade> => {
  validateTradeIdUuid(tradeId);
  const response = await authenticatedFetch(`/trades/${tradeId}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Trade not found");
    }
    await throwIfNotOk(response, `Failed to fetch trade: ${response.status}`);
  }

  return await response.json();
};

/**
 * Create new trade
 */
export const createTrade = async (
  tradeData: CreateTradeRequest
): Promise<BackendTrade> => {
  const response = await authenticatedFetch("/trades", {
    method: "POST",
    body: JSON.stringify(tradeData),
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to create trade: ${response.status}`);
  }

  return await response.json();
};

/**
 * Update existing trade (tradeId must be the trade's DB UUID).
 */
export const updateTrade = async (
  tradeId: string,
  updates: UpdateTradeRequest
): Promise<BackendTrade> => {
  validateTradeIdUuid(tradeId);
  const response = await authenticatedFetch(`/trades/${tradeId}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Trade not found");
    }
    await throwIfNotOk(response, `Failed to update trade: ${response.status}`);
  }

  return await response.json();
};

/**
 * Delete trade (tradeId must be the trade's DB UUID).
 */
export const deleteTrade = async (tradeId: string): Promise<void> => {
  validateTradeIdUuid(tradeId);
  const response = await authenticatedFetch(`/trades/${tradeId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Trade not found");
    }
    await throwIfNotOk(response, `Failed to delete trade: ${response.status}`);
  }

  // 204 No Content - no response body
};

/**
 * Delete all trades
 * @param brokerAccountCode Optional broker account code to filter trades by account
 * @param brokerName Optional broker slug when scoping with broker_account_code
 */
export const deleteAllTrades = async (
  brokerAccountCode?: string,
  brokerName?: string
): Promise<void> => {
  const params = new URLSearchParams();

  if (brokerAccountCode) {
    params.append("broker_account_code", brokerAccountCode);
  }
  if (brokerName?.trim()) {
    params.append("broker_name", brokerName.trim().toLowerCase());
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/trades?${queryString}` : "/trades";

  const response = await authenticatedFetch(endpoint, {
    method: "DELETE",
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to delete all trades: ${response.status}`);
  }

  // 204 No Content - no response body
};

/**
 * Close a single trade at the broker (POST /api/trades/{trade_id}/close).
 * Uses DB trade UUID; backend derives position_code from broker_trade_id.
 */
export interface CloseTradeResponse {
  message: string;
  trade_id: string;
  position_code: string;
  account_code: string;
}

export const closeTrade = async (tradeId: string): Promise<CloseTradeResponse> => {
  validateTradeIdUuid(tradeId);
  const response = await authenticatedFetch(`/trades/${tradeId}/close`, {
    method: "POST",
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new Error("Trade is not open or cannot be closed");
    }
    if (response.status === 404) {
      throw new Error("Trade not found");
    }
    if (response.status === 403) {
      throw new Error("Not allowed to close this trade");
    }
    await throwIfNotOk(response, `Failed to close trade: ${response.status}`);
  }

  return await response.json();
};

/**
 * Close all open positions for the current user's account (POST /api/broker/positions/close-all).
 */
export const closeAllTrades = async (): Promise<{ message: string; positions_closed?: number }> => {
  const response = await authenticatedFetch("/broker/positions/close-all", {
    method: "POST",
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to close all positions: ${response.status}`);
  }

  return await response.json();
};

/**
 * Sync trades from broker
 */
export const syncTradesFromBroker = async (
  accountCode?: string,
  startDate?: Date,
  endDate?: Date,
  brokerName?: string | null
): Promise<SyncTradesResponse | null> => {
  const params = new URLSearchParams();
  params.append("save_to_db", "true");

  if (brokerName) {
    params.append("broker_name", brokerName.toLowerCase());
  }
  if (accountCode) {
    params.append("account_code", accountCode);
  }
  if (startDate) {
    params.append("start_date", startDate.toISOString());
  }
  if (endDate) {
    params.append("end_date", endDate.toISOString());
  }

  const response = await authenticatedFetch(
    `/broker/trades?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        "Pragma": "no-cache",
        "Expires": "0",
      },
    }
  );

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to sync trades: ${response.status}`);
  }

  const text = await response.text().catch(() => "");
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as SyncTradesResponse;
  } catch {
    return null;
  }
};
